import { useState } from 'react';

interface FloatingLabelInputProps {
  label: string;
  type?: 'text' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  icon?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  containerHeight?: string;
  bottomOnly?: boolean;
  textSize?: string;
  labelSize?: string;
  rightAction?: {
    icon: string;
    label: string;
    onClick?: () => void;
  };
}

export function FloatingLabelInput({
  label,
  type = 'text',
  value,
  onChange,
  icon,
  disabled = false,
  className = '',
  error = false,
  errorMessage = '',
  containerHeight = 'h-[70px]',
  bottomOnly = false,
  textSize = 'text-[14px]',
  labelSize = 'text-[10px]',
  rightAction,
}: FloatingLabelInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const hasValue = value.length > 0;
  const isFloating = isFocused || hasValue;
  // Toggle visibility for password fields. We swap the input type so the
  // value isn't re-rendered (and the cursor position is preserved across
  // toggles).
  const isPassword = type === 'password';
  const effectiveType = isPassword && showPassword ? 'text' : type;

  return (
    <div className={className}>
      <div className={`${containerHeight} relative transition-colors ${
        bottomOnly 
          ? `border-b ${error ? 'border-red-500' : 'border-[#eee]'}` 
          : `bg-white rounded-[8px] border ${error ? 'border-red-500' : 'border-[#eee]'}`
      }`}>
        <input
          type={effectiveType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          className={`w-full h-full bg-transparent border-none outline-none ${bottomOnly ? 'px-0' : 'px-5'} ${rightAction ? 'pr-20' : (icon || isPassword) ? 'pr-10' : ''} ${containerHeight === 'h-[60px]' ? 'pt-5 pb-1' : 'pt-7 pb-2'} font-['DM_Sans',sans-serif] font-bold text-[#3a3335] ${textSize} tracking-[-0.1px] ${disabled ? 'cursor-default select-none' : 'cursor-text'}`}
          style={{ fontVariationSettings: "'opsz' 14" }}
        />
        <label
          className={`absolute ${bottomOnly ? 'left-0' : 'left-5'} font-['DM_Sans',sans-serif] tracking-[-0.1px] pointer-events-none transition-all duration-200 ${
            error ? 'text-red-500' : 'text-[#908e8e]'
          } ${
            isFloating
              ? `${containerHeight === 'h-[50px]' ? 'top-[4px]' : containerHeight === 'h-[60px]' ? 'top-[6px]' : containerHeight === 'h-[70px]' ? 'top-[10px]' : 'top-[12px]'} ${labelSize} font-normal leading-[18px]`
              : 'top-1/2 -translate-y-1/2 text-[14px] font-bold leading-[21px]'
          }`}
          style={{ fontVariationSettings: "'opsz' 14" }}
        >
          {label}
        </label>
        {isPassword && !rightAction && (
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            tabIndex={-1}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className={`absolute ${bottomOnly ? 'right-0' : 'right-[18px]'} top-1/2 -translate-y-1/2 flex items-center justify-center transition-colors ${
              error ? 'text-red-500' : 'text-[#908e8e] hover:text-[#3a3335]'
            }`}
          >
            <span className="material-icons !w-[20px] !h-[20px] !text-[20px] flex items-center justify-center">
              {showPassword ? 'visibility_off' : 'visibility'}
            </span>
          </button>
        )}
        {rightAction && (
          <button
            type="button"
            onClick={rightAction.onClick}
            className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2 text-[#908e8e] hover:text-[#3a3335] transition-colors font-['DM_Sans',sans-serif] text-[14px]"
          >
            <span className="material-icons !w-[18px] !h-[18px] !text-[18px] flex items-center justify-center">{rightAction.icon}</span>
            <span className="font-medium">{rightAction.label}</span>
          </button>
        )}
        {icon && !rightAction && !isPassword && (
          <span
            className={`absolute ${bottomOnly ? 'right-0' : 'right-[25px]'} top-1/2 -translate-y-1/2 !w-[18px] !h-[18px] !text-[18px] flex items-center justify-center pointer-events-none material-icons transition-colors ${
              error ? 'text-red-500' : hasValue ? 'text-[#3a3335]' : 'text-[#908e8e]'
            }`}
            style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100, 'wght' 400" }}
          >
            {icon}
          </span>
        )}
      </div>
      {error && errorMessage && (
        <p className="mt-1 text-red-500 text-[12px] font-['DM_Sans',sans-serif] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
          {errorMessage}
        </p>
      )}
    </div>
  );
}
